/**
 * Chromium only spell checks an editable element whose focus came from a user gesture. Internally,
 * Blink's IdleSpellCheckController ignores any focuses with FocusType::kScript, which is what
 * HTMLElement.focus() produces. This means that programmatic ways of entering or creating note
 * parts will not fire the spell checker (enter, arrow-key navigation, etc).
 *
 * However, you can still fire the spell checker via RespondToChangedContents--this is the only activation
 * method still not gated on gesture focus. So, if you fire a synthetic input event that reaches it, a full
 * spell-check of the focused editable will run. We therefore run a
 * document.execCommand('insertHTML', false, ''); to restore the red squiggles that should have been there.
 *
 * Interestingly, Chromium added the gesture requirement in 143 so that pages cannot mine the user's custom
 * dictionary: https://explainers-by-googlers.github.io/user-dictionary-leaks/.
 */

let rearming = false;
let composing = false;

if (typeof document !== 'undefined') {
  // Tracked at the document level so the rearm can be skipped mid-composition from any editable.
  document.addEventListener(
    'compositionstart',
    () => {
      composing = true;
    },
    true
  );
  document.addEventListener(
    'compositionend',
    () => {
      composing = false;
    },
    true
  );
}

/**
 * True while {@link rearmSpellCheck} is applying its no-op editing command.
 */
export function isRearmingSpellCheck(): boolean {
  return rearming;
}

/**
 * Re-arms Chromium's spell-checker by injecting a synthetic input event onto the provided DOM element.
 * This triggers Chromium/Blink's IdleSpellCheckController::RespondToChangedContents to flag the note part
 * again for spelling errors.
 */
export function rearmSpellCheck(containerEl: Element, collapsed: boolean): void {
  // Make sure we're not in the middle of composing a character, and our selection range === 0.
  if (!collapsed || composing) {
    return;
  }
  const editable = containerEl as HTMLElement;
  // Make sure spell-check is enabled, either on us or on a parent div. Make sure we're focused.
  if (!editable.spellcheck || document.activeElement !== editable) {
    return;
  }
  // Make sure we only run this extra command as part of a real user-activated editing window, and not
  // for non-interactive paths (forceUpdate, hash navigation, table cell editors).
  const activation = navigator.userActivation as UserActivation | undefined;
  if (!activation?.isActive) {
    return;
  }

  rearming = true;
  try {
    document.execCommand('insertHTML', false, '');
  } finally {
    rearming = false;
  }
}
